import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Select from '@/components/base/Select';
import Alert from '@/components/base/Alert';
import { adminApi } from '@/lib/adminApi';
import type { AdminClient, AdminEmailLog, AdminEmailTemplate } from '@/lib/adminApi';
import { formatDate } from '@/lib/format';
import { AdminCard, EmptyBlock, ErrorBlock, LoadingBlock, Modal } from './ui';

const STATUS_TONES: Record<string, string> = {
  sent: 'bg-primary-100 text-primary-800 border-primary-200',
  failed: 'bg-accent-100 text-accent-900 border-accent-200',
  queued: 'bg-secondary-100 text-secondary-800 border-secondary-200',
  not_configured: 'bg-background-200 text-foreground-700 border-background-300',
};

const STATUS_ICONS: Record<string, string> = {
  sent: 'ri-mail-check-line',
  failed: 'ri-mail-close-line',
  queued: 'ri-mail-line',
  not_configured: 'ri-mail-forbid-line',
};

function MailBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold font-label ${
        STATUS_TONES[status] ?? STATUS_TONES.not_configured
      }`}
    >
      <span className="flex items-center justify-center w-3 h-3">
        <i className={`${STATUS_ICONS[status] ?? 'ri-mail-line'} text-[11px]`} aria-hidden="true" />
      </span>
      {t(`admin.emailState.${status}`)}
    </span>
  );
}

export default function MessagingTab({ token }: { token: string }) {
  const { t, i18n } = useTranslation();
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [templates, setTemplates] = useState<AdminEmailTemplate[]>([]);
  const [history, setHistory] = useState<AdminEmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const [recipient, setRecipient] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [templateKey, setTemplateKey] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AdminEmailLog | null>(null);

  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const [emailsRes, clientsRes, templatesRes] = await Promise.all([
        adminApi.emailsList(token),
        adminApi.clientsList(token),
        adminApi.emailTemplates(token),
      ]);
      if (!emailsRes.ok) {
        setFailed(true);
        return;
      }
      setHistory((emailsRes.emails ?? []) as AdminEmailLog[]);
      if (clientsRes.ok) setClients((clientsRes.clients ?? []) as AdminClient[]);
      if (templatesRes.ok) setTemplates((templatesRes.templates ?? []) as AdminEmailTemplate[]);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const withEmail = useMemo(() => clients.filter((client) => Boolean(client.email)), [clients]);

  const recipientOptions = withEmail.map((client) => ({
    value: client.email as string,
    label: `${client.fullName} — ${client.email}`,
  }));

  const applyTemplate = (key: string) => {
    setTemplateKey(key);
    if (!key) return;
    const template = templates.find((item) => item.key === key);
    if (!template) return;
    setSubject(template.subject);
    setMessage(template.message);
    setNotice('');
    setError('');
  };

  const wrapSelection = (open: string, close: string) => {
    const el = bodyRef.current;
    if (!el) {
      setMessage((prev) => `${prev}${open}${close}`);
      return;
    }
    const start = el.selectionStart ?? message.length;
    const end = el.selectionEnd ?? message.length;
    const selected = message.slice(start, end);
    const next = `${message.slice(0, start)}${open}${selected}${close}${message.slice(end)}`;
    setMessage(next);
  };

  const insertBreak = () => {
    const el = bodyRef.current;
    const position = el?.selectionStart ?? message.length;
    setMessage(`${message.slice(0, position)}<br/>${message.slice(position)}`);
  };

  const previewHtml = `<!doctype html><html><head><meta charset="utf-8"/><style>
    body{margin:0;padding:20px;background:#f4f4f2;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif}
    .card{max-width:560px;margin:0 auto;background:#fff;border:1px solid #e6e6e6;border-radius:14px;overflow:hidden}
    .top{background:#0b1520;padding:18px 24px;color:#fff;font-weight:700;letter-spacing:.05em;font-size:15px}
    .body{padding:24px;font-size:14px;line-height:1.7;color:#3d3d3d}
    h2{margin:0 0 12px;font-size:18px;color:#111}
    .foot{padding:14px 24px;background:#fafafa;border-top:1px solid #ececec;font-size:11px;color:#9a9a9a}
  </style></head><body><div class="card"><div class="top">Monexa</div><div class="body"><h2>${
    subject || 'Objet du message'
  }</h2>${message || '<p>Aucun contenu.</p>'}</div><div class="foot">Notification automatique — merci de ne pas répondre.</div></div></body></html>`;

  const send = async () => {
    if (sending) return;
    setNotice('');
    setError('');

    if (!recipient.trim() || !subject.trim() || !message.trim()) {
      setError(t('admin.mail.missing'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(recipient.trim())) {
      setError(t('admin.mail.invalidEmail'));
      return;
    }

    setSending(true);
    const res = await adminApi.emailSend(token, {
      to: recipient.trim(),
      recipientName,
      subject: subject.trim(),
      message,
      templateKey,
    });
    setSending(false);

    if (!res.ok) {
      setError(
        res.code === 'INVALID_EMAIL' ? t('admin.mail.invalidEmail') : t('errors.server'),
      );
      return;
    }

    const status = String(res.status ?? '');
    setNotice(status === 'sent' ? t('admin.mail.sent') : t('admin.mail.notSent'));
    setSubject('');
    setMessage('');
    setTemplateKey('');
    load();
  };

  const handleRecipientChange = (email: string) => {
    setRecipient(email);
    const client = withEmail.find((item) => item.email === email);
    setRecipientName(client ? client.fullName : '');
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_1fr] gap-5 items-start">
      <AdminCard title={t('admin.mail.compose')} subtitle={t('admin.mail.composeHint')}>
        <div className="flex flex-col gap-3.5">
          <Select
            label={t('admin.mail.recipient')}
            name="recipient"
            value={recipient}
            onChange={(event) => handleRecipientChange(event.target.value)}
            options={recipientOptions}
            placeholder={t('admin.mail.recipientPh')}
            icon="ri-user-line"
          />

          <Input
            label={t('admin.mail.recipientManual')}
            name="recipientManual"
            type="email"
            value={recipient}
            onChange={(event) => handleRecipientChange(event.target.value)}
            placeholder="client@email.com"
            icon="ri-mail-line"
            autoComplete="off"
          />

          <div>
            <p className="mb-2 text-[13px] font-semibold text-foreground-700 font-label">
              {t('admin.mail.templates')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {templates.length === 0 ? (
                <span className="text-[12px] text-foreground-400">{t('admin.common.none')}</span>
              ) : (
                templates.map((template) => (
                  <button
                    key={template.key}
                    type="button"
                    onClick={() => applyTemplate(template.key === templateKey ? '' : template.key)}
                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-[11.5px] font-semibold font-label whitespace-nowrap transition-colors cursor-pointer ${
                      templateKey === template.key
                        ? 'border-primary-300 bg-primary-50 text-primary-800'
                        : 'border-background-300 bg-background-100 text-foreground-700 hover:border-primary-300'
                    }`}
                  >
                    <span className="flex items-center justify-center w-3.5 h-3.5">
                      <i className="ri-file-text-line text-[13px]" aria-hidden="true" />
                    </span>
                    {t(`admin.mail.tpl.${template.key}`)}
                  </button>
                ))
              )}
            </div>
          </div>

          <Input
            label={t('admin.mail.subject')}
            name="subject"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder={t('admin.mail.subjectPh')}
            icon="ri-text"
          />

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[13px] font-semibold text-foreground-700 font-label">
                {t('admin.mail.message')}
              </span>
              <span className="text-[11px] text-foreground-400 font-label tabular">
                {message.length} / 20000
              </span>
            </div>

            <div className="flex items-center gap-1 mb-2 flex-wrap">
              <button
                type="button"
                onClick={() => wrapSelection('<strong>', '</strong>')}
                aria-label="bold"
                className="flex items-center justify-center w-8 h-8 rounded-lg border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <i className="ri-bold text-[13px]" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => wrapSelection('<em>', '</em>')}
                aria-label="italic"
                className="flex items-center justify-center w-8 h-8 rounded-lg border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <i className="ri-italic text-[13px]" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={insertBreak}
                aria-label="line break"
                className="flex items-center justify-center w-8 h-8 rounded-lg border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <i className="ri-text-wrap text-[13px]" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-background-300 text-[11.5px] font-semibold text-foreground-700 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center justify-center w-3.5 h-3.5">
                  <i className="ri-eye-line text-[13px]" aria-hidden="true" />
                </span>
                {t('admin.mail.preview')}
              </button>
            </div>

            <textarea
              ref={bodyRef}
              name="message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              rows={10}
              maxLength={20000}
              placeholder={t('admin.mail.messagePh')}
              className="w-full box-border rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-[14px] leading-relaxed text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors hover:border-background-400 focus:border-primary-500 resize-y"
            />
          </div>

          {notice ? <Alert tone="success">{notice}</Alert> : null}
          {error ? <Alert tone="error">{error}</Alert> : null}

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              fullWidth
              loading={sending}
              onClick={send}
              icon={<i className="ri-send-plane-line" aria-hidden="true" />}
            >
              {t('admin.mail.send')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setPreviewOpen(true)}
              icon={<i className="ri-eye-line" aria-hidden="true" />}
            >
              {t('admin.mail.preview')}
            </Button>
          </div>

          <Alert tone="info">{t('admin.mail.deliveryInfo')}</Alert>
        </div>
      </AdminCard>

      <AdminCard title={t('admin.mail.history')} subtitle={t('admin.mail.historyHint')} padded={false}>
        {loading ? (
          <LoadingBlock />
        ) : failed ? (
          <ErrorBlock onRetry={load} />
        ) : history.length === 0 ? (
          <EmptyBlock label={t('admin.mail.empty')} icon="ri-mail-line" />
        ) : (
          <ul className="divide-y divide-background-200 max-h-[720px] overflow-y-auto">
            {history.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => setSelectedLog(row)}
                  className="w-full text-left px-5 py-3 hover:bg-background-100/70 transition-colors cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-foreground-950 truncate">{row.subject}</p>
                      <p className="mt-0.5 text-[11.5px] text-foreground-500 break-all">
                        {row.recipientName ? `${row.recipientName} · ` : ''}
                        {row.recipientEmail}
                      </p>
                      <p className="mt-0.5 text-[11px] text-foreground-400">
                        {formatDate(row.createdAt, i18n.language, true)}
                      </p>
                    </div>
                    <div className="shrink-0">
                      <MailBadge status={row.status} />
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </AdminCard>

      <Modal
        open={previewOpen}
        title={t('admin.mail.preview')}
        subtitle={subject || undefined}
        onClose={() => setPreviewOpen(false)}
        width="max-w-2xl"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setPreviewOpen(false)}>
              {t('admin.common.close')}
            </Button>
            <Button size="sm" loading={sending} onClick={send} icon={<i className="ri-send-plane-line" aria-hidden="true" />}>
              {t('admin.mail.send')}
            </Button>
          </>
        }
      >
        <iframe
          title={t('admin.mail.preview')}
          srcDoc={previewHtml}
          className="w-full h-[460px] rounded-xl border border-background-200 bg-background-100"
        />
      </Modal>

      <Modal
        open={Boolean(selectedLog)}
        title={selectedLog?.subject ?? ''}
        subtitle={selectedLog?.recipientEmail}
        onClose={() => setSelectedLog(null)}
        width="max-w-2xl"
      >
        {selectedLog ? (
          <div className="flex flex-col gap-3.5">
            <div className="flex flex-wrap items-center gap-2">
              <MailBadge status={selectedLog.status} />
              <span className="text-[11.5px] text-foreground-500 font-label">
                {formatDate(selectedLog.createdAt, i18n.language, true)}
              </span>
              {selectedLog.sentBy ? (
                <span className="text-[11.5px] text-foreground-400 font-label">
                  · {selectedLog.sentBy}
                </span>
              ) : null}
            </div>
            <iframe
              title={selectedLog.subject}
              srcDoc={selectedLog.body}
              className="w-full h-[420px] rounded-xl border border-background-200 bg-background-100"
            />
            {selectedLog.error ? (
              <Alert tone="error" title={t('admin.transfers.emailError')}>
                <span className="break-all font-label text-[11.5px]">{selectedLog.error}</span>
              </Alert>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}